import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useRxsForPatient } from "../../../stores/Rx/rx.queries";
import {
  buildPatientSearchLabel,
  usePatientLocationSearch,
  PatientSearchOption,
} from "../../../stores/Patients/patient.search";
import {
  renderPatientSearchGroup,
  renderPatientSearchOption,
} from "../../../molecules/PatientSearchOption";
import { BaseResultDtoKindEnum } from "../../../api-client";
import DoctorClinicAutocomplete from "../../PatientOverview/RxInfo/RxForms/DoctorClinicAutocomplete";

export interface LinkageChanges {
  patientId?: number;
  patient?: { id?: number; firstName?: string; lastName?: string; dob?: string };
  rxId?: string;
  rx?: { id?: string; rxNumber?: string };
  doctorId?: string;
  doctor?: { id?: string; contactInfo?: { firstNames?: string; lastName?: string } };
  clinicId?: string;
  clinic?: { id?: string; contactInfo?: { businessName?: string } };
}

interface SelectedDoctor {
  doctorId: string;
  clinicId: string;
  label: string;
  doctor: any;
  clinic: any;
}

interface LinkagesEditorProps {
  patientId?: number;
  patient?: { id?: number; firstName?: string; lastName?: string; dob?: string };
  rxId?: string;
  rxNumber?: string;
  doctorId?: string;
  doctorLabel?: string;
  clinicId?: string;
  onSave: (changes: LinkageChanges) => Promise<void> | void;
  onCancel: () => void;
  /**
   * MD-334: reports edits the user has made but not yet saved, so whoever hosts
   * this editor can ask before dropping them. Called with a handle that commits
   * those edits while they are pending, and with null as soon as they are gone -
   * saved, reverted, or the editor unmounted (Abbrechen is an explicit discard,
   * so it clears the handle rather than raising a prompt).
   */
  onPendingChange?: (pending: { save: () => Promise<void> } | null) => void;
}

const formatDob = (dob?: string) => {
  if (!dob) return "";
  try {
    return format(new Date(dob), "dd.MM.yyyy");
  } catch {
    return "";
  }
};

/**
 * Wrap the patient already linked to the ticket so the Autocomplete can show it
 * with the exact label the search results use.
 */
const toSelectedPatient = (
  id: number,
  patient?: { firstName?: string; lastName?: string; dob?: string }
): PatientSearchOption => ({
  kind: BaseResultDtoKindEnum.Patient,
  id,
  patientId: id,
  firstName: patient?.firstName,
  lastName: patient?.lastName,
  dob: patient?.dob,
  label: buildPatientSearchLabel({ ...patient, patientId: id }),
  locationMatch: true,
  group: "Here",
});

const LinkagesEditor: React.FC<LinkagesEditorProps> = ({
  patientId,
  patient,
  rxId,
  rxNumber,
  doctorId,
  doctorLabel,
  clinicId,
  onSave,
  onCancel,
  onPendingChange,
}) => {
  const [patientSearch, setPatientSearch] = useState("");
  const [selectedPatient, setSelectedPatient] = useState<PatientSearchOption | null>(
    patientId ? toSelectedPatient(patientId, patient) : null
  );
  const [selectedRxId, setSelectedRxId] = useState<string | null>(rxId ?? null);
  const [selectedDoctor, setSelectedDoctor] = useState<SelectedDoctor | null>(
    doctorId
      ? { doctorId, clinicId: clinicId ?? "", label: doctorLabel ?? "", doctor: null, clinic: null }
      : null
  );
  const [isSaving, setIsSaving] = useState(false);

  // Same search the app bar uses: hits from the location the user currently
  // works in are grouped first, everything else follows under its own heading.
  const { options: patientOptions, loading: patientsLoading } = usePatientLocationSearch(
    patientSearch,
    { patientsOnly: true }
  );

  // The patient the ticket arrived with, kept independent of the current
  // selection. MD-335: pinning only the selection meant clearing the field with
  // the x made the original patient unreachable - the search needs a minimum
  // term length, so an empty box offers nothing to pick from and the only way
  // back was to cancel the whole edit.
  const initialPatient = useMemo(
    () => (patientId ? toSelectedPatient(patientId, patient) : null),
    [patientId, patient]
  );

  // Neither the linked nor the originally-linked patient is part of the search
  // result set, so keep both in the option list - otherwise the Autocomplete
  // holds a value it cannot render, or drops a value the user wants back.
  const patientOptionsWithSelection = useMemo(() => {
    const pinned: PatientSearchOption[] = [];
    for (const candidate of [selectedPatient, initialPatient]) {
      if (!candidate) continue;
      if (pinned.some((p) => p.patientId === candidate.patientId)) continue;
      if (patientOptions.some((o) => o.patientId === candidate.patientId)) continue;
      pinned.push(candidate);
    }
    return pinned.length ? [...pinned, ...patientOptions] : patientOptions;
  }, [patientOptions, selectedPatient, initialPatient]);

  const effectivePatientId = selectedPatient?.patientId ?? 0;
  const { data: rxList = [], isLoading: rxLoading } = useRxsForPatient(effectivePatientId);

  const rxOptions = useMemo(
    () => rxList.filter((rx) => !rx.deletedAt),
    [rxList]
  );

  const selectedRxOption = useMemo(
    () => rxOptions.find((rx) => rx.id === selectedRxId) ?? null,
    [rxOptions, selectedRxId]
  );

  const handlePatientChange = (_e: any, value: PatientSearchOption | null) => {
    if (value?.isEmptyPlaceholder) return;
    setSelectedPatient(value);
    // The currently selected Rx belongs to the previous patient, so it is no
    // longer valid once the patient changes. Force a re-selection.
    if (value?.patientId !== selectedPatient?.patientId) {
      setSelectedRxId(null);
    }
  };

  const patientChanged = (selectedPatient?.patientId ?? undefined) !== (patientId ?? undefined);
  const rxNeedsReselect = patientChanged && !selectedRxId;

  // MD-335 (Monique, Befund 3): emptying the Rezept box cannot actually unlink
  // the Rezept, and the editor used to say nothing about it.
  //
  // Emptying it by ANY route clears the selection - the x, and deleting the
  // text too, because MUI's `handleInputChange` calls `handleValue(_, null,
  // "clear")` as soon as the input reads "". But `changes` below only records
  // an rxId when one is actually selected, so a cleared Rezept produces no
  // change at all: `isDirty` stays false, Speichern falls through to
  // `onCancel()`, the editor closes and the display re-reads the ticket's
  // original Rezept. That is the "old value is back, without a message" Monique
  // saw - the reset happens on Speichern, not on blur.
  //
  // Requiring a Rezept is fine per the ticket; being told is what was missing.
  // So the link stands and we say so, rather than claiming the field is still
  // holding a value it has already dropped.
  const rxClearedButStillLinked = !!rxId && !selectedRxId && !rxNeedsReselect;

  // The diff between what the ticket is linked to and what is selected here.
  // Both the Speichern button and the unsaved-changes prompt (MD-334) read this,
  // so "is there something to save" and "what gets saved" cannot drift apart.
  const changes = useMemo(() => {
    const next: LinkageChanges = {};

    if ((selectedPatient?.patientId ?? undefined) !== (patientId ?? undefined) && selectedPatient?.patientId) {
      next.patientId = selectedPatient.patientId;
      next.patient = {
        id: selectedPatient.patientId,
        firstName: selectedPatient.firstName,
        lastName: selectedPatient.lastName,
        dob: selectedPatient.dob,
      };
    }

    if ((selectedRxId ?? undefined) !== (rxId ?? undefined) && selectedRxId) {
      next.rxId = selectedRxId;
      const rx = rxOptions.find((r) => r.id === selectedRxId);
      next.rx = rx ? { id: rx.id, rxNumber: rx.rxNumber } : undefined;
    }

    if ((selectedDoctor?.doctorId ?? undefined) !== (doctorId ?? undefined) && selectedDoctor?.doctorId) {
      next.doctorId = selectedDoctor.doctorId;
      next.doctor = selectedDoctor.doctor
        ? { id: selectedDoctor.doctor.id, contactInfo: selectedDoctor.doctor.contactInfo }
        : { id: selectedDoctor.doctorId };
      if (selectedDoctor.clinicId) {
        next.clinicId = selectedDoctor.clinicId;
        next.clinic = selectedDoctor.clinic
          ? { id: selectedDoctor.clinic.id, contactInfo: selectedDoctor.clinic.contactInfo }
          : { id: selectedDoctor.clinicId };
      }
    }

    return next;
  }, [selectedPatient, selectedRxId, selectedDoctor, patientId, rxId, doctorId, rxOptions]);

  const isDirty = Object.keys(changes).length > 0;

  const commit = async () => {
    try {
      setIsSaving(true);
      await onSave(changes);
    } finally {
      setIsSaving(false);
    }
  };

  // Held in a ref so the handle handed upward always commits the current
  // selection, without the effect below re-running on every selection change.
  const commitRef = useRef(commit);
  commitRef.current = commit;

  useEffect(() => {
    if (!onPendingChange) return;
    onPendingChange(isDirty ? { save: () => commitRef.current() } : null);
    return () => onPendingChange(null);
  }, [isDirty, onPendingChange]);

  const handleSave = async () => {
    if (!isDirty) {
      onCancel();
      return;
    }
    await commit();
  };

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      {/* Patient selector */}
      <Autocomplete<PatientSearchOption, false, false, false>
        size="small"
        options={patientOptionsWithSelection}
        loading={patientsLoading}
        value={selectedPatient}
        onChange={handlePatientChange}
        onInputChange={(_e, value, reason) => {
          if (reason === "input") setPatientSearch(value);
        }}
        // The backend already filtered and ranked by location - re-filtering
        // client side would drop valid hits.
        filterOptions={(options) => options}
        groupBy={(option) => option.group}
        getOptionDisabled={(option) => !!option.isEmptyPlaceholder}
        isOptionEqualToValue={(option, value) => option.patientId === value.patientId}
        getOptionLabel={(option) => option.label}
        renderGroup={renderPatientSearchGroup}
        renderOption={renderPatientSearchOption}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Patient"
            placeholder="Patient suchen…"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {patientsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {/* Rx selector (scoped to the selected patient) */}
      <Autocomplete
        size="small"
        options={rxOptions}
        loading={rxLoading}
        disabled={!selectedPatient}
        value={selectedRxOption}
        onChange={(_e, value) => setSelectedRxId(value?.id ?? null)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => {
          const therapies = (option.therapyRxs ?? [])
            .map((t) => `${t.amount}x${t.therapyAbbr ?? ""}`)
            .filter(Boolean)
            .join(", ");
          const date = option.date ? ` · ${formatDob(option.date as unknown as string)}` : "";
          return `${option.rxNumber ?? ""}${date}${therapies ? ` · ${therapies}` : ""}`.trim();
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Rezept"
            placeholder={selectedPatient ? "Rezept wählen…" : "Zuerst Patient wählen"}
            error={rxNeedsReselect}
            helperText={
              rxNeedsReselect
                ? "Bitte Rezept für den neuen Patienten wählen"
                : rxClearedButStillLinked
                  ? "Ein Rezept ist erforderlich – die bisherige Verknüpfung bleibt bestehen. Zum Wechseln ein anderes Rezept wählen."
                  : undefined
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {rxLoading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {/* Doctor / clinic selector */}
      <Box>
        {selectedDoctor?.label && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Aktuell: {selectedDoctor.label}
          </Typography>
        )}
        <DoctorClinicAutocomplete
          placeholder="Arzt / Praxis suchen…"
          initialInputValue={selectedDoctor?.label ?? ""}
          onSelect={(doctor, clinic) => {
            const label = `Dr. ${doctor.contactInfo?.firstNames ?? ""} ${
              doctor.contactInfo?.lastName ?? ""
            }`.trim();
            setSelectedDoctor({
              doctorId: doctor.id,
              clinicId: clinic?.id ?? "",
              label,
              doctor,
              clinic,
            });
          }}
          onAddNew={() => {}}
        />
      </Box>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button size="small" onClick={onCancel} disabled={isSaving}>
          Abbrechen
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Speichern
        </Button>
      </Stack>
    </Stack>
  );
};

export default LinkagesEditor;
